import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';
import { cn } from 'src/utils/utils';

import { Typography } from './typography';

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipRoot = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipPortal = TooltipPrimitive.Portal;

const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Arrow>
>(({ className, ...props }, ref) => (
  <TooltipPrimitive.Arrow
    ref={ref}
    className={cn('tw:fill-white', className)}
    width={16}
    height={6}
    {...props}
  />
));
TooltipArrow.displayName = TooltipPrimitive.Arrow.displayName;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    showArrow?: boolean;
  }
>(
  (
    { className, sideOffset = 4, showArrow = false, children, ...props },
    ref
  ) => (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'tw:overflow-hidden tw:rounded-bw-2xl-3xl tw:bg-logo-white tw:px-m tw:py-s tw:text-text-primary tw:border tw:border-border-primary ',
          className
        )}
        {...props}
      >
        {children}
        {!!showArrow && <TooltipArrow />}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * Props for the Tooltip component
 */
export interface TooltipProps {
  /** Trigger element that shows the tooltip on hover */
  trigger: React.ReactNode;
  /** Main tooltip text/title */
  title?: string;
  /** Optional supporting/description text */
  description?: string;
  /** Side where the tooltip appears */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Horizontal alignment of the tooltip */
  align?: 'start' | 'center' | 'end';
  /** Whether to show the arrow pointer */
  showArrow?: boolean;
  /** Side offset for the tooltip content */
  sideOffset?: number;
  /** Delay duration before showing tooltip (ms) */
  delayDuration?: number;
  /** Additional className merged onto the trigger element itself. Also
   *  receives any className injected by an ancestor using `asChild`
   *  (e.g. DropdownMenuItem), so the trigger absorbs that styling directly
   *  instead of it landing on the (invisible) wrapper span. */
  className?: string;
  /** Additional className for the trigger wrapper span */
  triggerClassName?: string;
  /** Additional className merged onto the tooltip's popup content box */
  contentClassName?: string;
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Tooltip Component
 *
 * A flexible tooltip component built on Radix UI that supports:
 * - Simple title-only tooltips
 * - Tooltips with supporting description text
 * - Optional arrow pointer
 * - Multiple positioning options (top, bottom, left, right)
 * - Full keyboard accessibility
 *
 * @example
 * // Basic usage - wrap any element
 * <Tooltip trigger={<Button>Hover me</Button>} title="This is a tooltip" />
 *
 * @example
 * // With description text
 * <Tooltip
 *   trigger={<IconButton icon={InfoIcon} />}
 *   title="More Information"
 *   description="Tooltips help users understand the meaning or function of an element."
 *   showArrow
 * />
 *
 * @example
 * // SVG icons don't forward refs, so wrap them in a span
 * <Tooltip
 *   trigger={<span className="tw:inline-flex"><Icon icon={InfoIcon} /></span>}
 *   title="More info"
 * />
 *
 * @example
 * // Custom positioning
 * <Tooltip
 *   trigger={<span>Help</span>}
 *   title="Quick tip"
 *   side="right"
 *   align="start"
 * />
 */
export const Tooltip = React.forwardRef<HTMLElement, TooltipProps>(
  (
    {
      trigger,
      title,
      description,
      side = 'top',
      align = 'center',
      showArrow = false,
      sideOffset = 4,
      delayDuration = 200,
      className,
      triggerClassName,
      contentClassName,
      open,
      onOpenChange,
      // Any other props (e.g. onClick/onPointerDown injected by an ancestor
      // using asChild, such as Radix's DropdownMenuItem) must reach the real
      // trigger element, or that ancestor's own behavior (like closing a
      // menu on select) silently breaks.
      ...rest
    },
    ref
  ) => {
    const hasDescription = !!description;
    return (
      <TooltipProvider delayDuration={delayDuration}>
        <TooltipRoot open={open} onOpenChange={onOpenChange}>
          <TooltipTrigger
            asChild
            ref={ref as React.Ref<HTMLButtonElement>}
            className={cn('tw:inline-flex', className, triggerClassName)}
            {...rest}
          >
            {/* No wrapper: with `asChild` the trigger itself must receive the
                ref, classes and handlers (e.g. from a DropdownMenuItem). SVG
                icons don't forward refs (svgr has no `ref: true`), so callers
                wrap those in a <span>. */}
            {trigger}
          </TooltipTrigger>
          <TooltipContent
            side={side}
            align={align}
            sideOffset={sideOffset}
            showArrow={showArrow}
            className={cn(
              'tw:z-10000 tw:max-w-[280px] tw:max-h-[400px] tw:overflow-y-auto',
              hasDescription && 'tw:p-m',
              contentClassName
            )}
          >
            <div className={cn('tw:flex tw:flex-col tw:gap-xs')}>
              {!!title && (
                <Typography
                  variant="h6"
                  align={hasDescription ? 'left' : 'center'}
                >
                  {title}
                </Typography>
              )}
              {!!description && (
                <Typography
                  variant="b3"
                  align="left"
                  className="tw:whitespace-pre-line"
                >
                  {description}
                </Typography>
              )}
            </div>
          </TooltipContent>
        </TooltipRoot>
      </TooltipProvider>
    );
  }
);

Tooltip.displayName = 'Tooltip';

// Export primitives for advanced usage
export {
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger
};

export default Tooltip;
